/**
 * @file FilterHeader - Per-column inline filter UI for grid/table editors
 * @version 2.0.0
 *
 * Simplified filtering UX:
 * - Strings: regex search only (no operator selector)
 * - Booleans: rotating button (no filter → true → false)
 * - Numbers: input-group with >=, <=, = selector
 * - Dates: input-group with >=, <=, = selector and date picker
 * - Enums: dropdown multi-select (Excel-like)
 */

const { ref, watch, computed } = Vue;

// Operator definitions for numeric/date fields
const COMPARISON_OPERATORS = [
  { value: '$eq', label: '=' },
  { value: '$gte', label: '≥' },
  { value: '$lte', label: '≤' },
];

/**
 * Extract simple per-field conditions from a Mango selector.
 * @param {object} selector - Mango selector
 * @returns {object} Map of fieldName → filter state
 */
function selectorToFilters(selector) {
  const filters = {};

  if (!selector || Object.keys(selector).length === 0) {
    return filters;
  }

  const processCondition = condition => {
    const field = Object.keys(condition).find(k => !k.startsWith('$'));
    if (!field) return null;

    const value = condition[field];

    // Direct value: { field: value } → $eq
    if (typeof value !== 'object' || value === null) {
      return { field, operator: '$eq', value };
    }

    // Operator object
    const op = Object.keys(value)[0];
    if (!op || !op.startsWith('$')) {
      return { field, operator: '$eq', value };
    }

    // Handle $regex for string contains
    if (op === '$regex') {
      return { field, operator: '$regex', value: value[op] };
    }

    // Handle $in for enum multi-select
    if (op === '$in') {
      return { field, operator: '$in', value: value[op] };
    }

    return { field, operator: op, value: value[op] };
  };

  // Handle $and array
  if (selector.$and && Array.isArray(selector.$and)) {
    for (const condition of selector.$and) {
      const extracted = processCondition(condition);
      if (extracted) {
        filters[extracted.field] = {
          operator: extracted.operator,
          value: extracted.value,
        };
      }
    }
    return filters;
  }

  // Handle flat structure
  for (const [field, value] of Object.entries(selector)) {
    if (field.startsWith('$')) continue;
    const extracted = processCondition({ [field]: value });
    if (extracted) {
      filters[extracted.field] = {
        operator: extracted.operator,
        value: extracted.value,
      };
    }
  }

  return filters;
}

/**
 * Convert internal filters to Mango selector
 * @param {object} filters - Map of fieldName → { operator, value }
 * @returns {object} Mango selector
 */
function filtersToSelector(filters) {
  const conditions = [];

  for (const [field, filter] of Object.entries(filters)) {
    if (
      filter.value === '' ||
      filter.value === null ||
      filter.value === undefined
    ) {
      continue;
    }

    // Handle $in with empty array
    if (
      filter.operator === '$in' &&
      Array.isArray(filter.value) &&
      filter.value.length === 0
    ) {
      continue;
    }

    const { operator, value } = filter;

    if (operator === '$eq') {
      conditions.push({ [field]: value });
    } else if (operator === '$regex') {
      conditions.push({ [field]: { $regex: value } });
    } else if (operator === '$in') {
      conditions.push({ [field]: { $in: value } });
    } else {
      conditions.push({ [field]: { [operator]: value } });
    }
  }

  if (conditions.length === 0) {
    return {};
  }

  if (conditions.length === 1) {
    return conditions[0];
  }

  return { $and: conditions };
}

export default {
  name: 'FilterHeader',

  props: {
    schema: {
      type: Object,
      required: true,
    },
    fields: {
      type: Array,
      required: true,
    },
    query: {
      type: Object,
      default: () => ({ selector: {} }),
    },
    actionControlStyles: {
      type: Object,
      default: () => ({}),
    },
    fieldHeaderStyles: {
      type: Object,
      default: () => ({}),
    },
  },

  emits: ['update:query'],

  setup(props, { emit }) {
    const filters = ref({});

    // Track if we're emitting to avoid feedback loops
    let isEmitting = false;

    const initializeFilters = () => {
      const newFilters = {};
      for (const field of props.fields) {
        newFilters[field] = { operator: '$eq', value: '' };
      }
      const existing = selectorToFilters(props.query?.selector);
      for (const [field, filter] of Object.entries(existing)) {
        if (newFilters[field]) {
          newFilters[field] = filter;
        }
      }
      filters.value = newFilters;
    };

    initializeFilters();

    // Watch external query changes (e.g., from FilterBar)
    watch(
      () => props.query?.selector,
      newSelector => {
        if (isEmitting) return; // Skip if this is our own emit
        const external = selectorToFilters(newSelector);
        for (const field of props.fields) {
          if (external[field]) {
            filters.value[field] = external[field];
          } else if (filters.value[field]?.value) {
            filters.value[field] = { operator: '$eq', value: '' };
          }
        }
      },
      { deep: true }
    );

    watch(
      () => props.fields,
      () => initializeFilters(),
      { deep: true }
    );

    // Reset filters when schema changes
    watch(
      () => props.schema,
      () => {
        initializeFilters();
        emit('update:query', { selector: {} });
      }
    );

    const emitUpdate = () => {
      isEmitting = true;
      const selector = filtersToSelector(filters.value);
      emit('update:query', { selector });
      // Reset flag after Vue processes the update
      Vue.nextTick(() => {
        isEmitting = false;
      });
    };

    const hasActiveFilters = computed(() => {
      return Object.values(filters.value).some(f => {
        if (f.value === '' || f.value === null || f.value === undefined)
          return false;
        if (Array.isArray(f.value) && f.value.length === 0) return false;
        return true;
      });
    });

    const clearAll = () => {
      for (const field of props.fields) {
        filters.value[field] = { operator: '$eq', value: '' };
      }
      emitUpdate();
    };

    const updateFilter = (field, operator, value) => {
      if (filters.value[field]) {
        filters.value[field] = { operator, value };
        emitUpdate();
      }
    };

    return {
      filters,
      hasActiveFilters,
      clearAll,
      updateFilter,
    };
  },

  computed: {
    itemSchema() {
      return this.schema.items || this.schema;
    },

    comparisonOperators() {
      return COMPARISON_OPERATORS;
    },
  },

  methods: {
    /**
     * Get field type info
     * @param fieldName
     */
    getFieldType(fieldName) {
      const fieldSchema = this.itemSchema.properties?.[fieldName];
      if (!fieldSchema) return 'string';

      // Check for enum
      if (fieldSchema.enum) return 'enum';

      const type = fieldSchema.type;
      const format = fieldSchema.format;

      if (type === 'boolean') return 'boolean';
      if (type === 'number' || type === 'integer') return 'number';
      if (type === 'string' && (format === 'date' || format === 'date-time')) {
        return format === 'date-time' ? 'datetime' : 'date';
      }

      return 'string';
    },

    /**
     * Get enum options for a field
     * @param fieldName
     */
    getEnumOptions(fieldName) {
      const fieldSchema = this.itemSchema.properties?.[fieldName];
      return fieldSchema?.enum || [];
    },

    /**
     * Get HTML input type for date fields
     * @param fieldType
     */
    getDateInputType(fieldType) {
      return fieldType === 'datetime' ? 'datetime-local' : 'date';
    },

    // === String handlers ===
    onStringInput(field, event) {
      const value = event.target.value;
      this.updateFilter(field, '$regex', value);
    },

    // === Boolean handlers ===
    getBooleanState(field) {
      const filter = this.filters[field];
      if (
        !filter ||
        filter.value === '' ||
        filter.value === null ||
        filter.value === undefined
      ) {
        return 'none';
      }
      return filter.value === true ? 'true' : 'false';
    },

    getBooleanIcon(state) {
      switch (state) {
        case 'true':
          return 'bi-check-circle-fill text-success';
        case 'false':
          return 'bi-x-circle-fill text-danger';
        default:
          return 'bi-circle text-muted';
      }
    },

    getBooleanTitle(state) {
      switch (state) {
        case 'true':
          return 'Filtering: true (click for false)';
        case 'false':
          return 'Filtering: false (click to clear)';
        default:
          return 'No filter (click for true)';
      }
    },

    rotateBooleanFilter(field) {
      const current = this.getBooleanState(field);
      switch (current) {
        case 'none':
          this.updateFilter(field, '$eq', true);
          break;
        case 'true':
          this.updateFilter(field, '$eq', false);
          break;
        case 'false':
          this.updateFilter(field, '$eq', '');
          break;
      }
    },

    // === Numeric handlers ===
    onNumericOperatorChange(field, event) {
      const operator = event.target.value;
      const currentValue = this.filters[field]?.value ?? '';
      this.updateFilter(field, operator, currentValue);
    },

    onNumericValueChange(field, event) {
      const value = event.target.value === '' ? '' : Number(event.target.value);
      const operator = this.filters[field]?.operator || '$eq';
      this.updateFilter(field, operator, value);
    },

    // === Date handlers ===
    onDateOperatorChange(field, event) {
      const operator = event.target.value;
      const currentValue = this.filters[field]?.value ?? '';
      this.updateFilter(field, operator, currentValue);
    },

    onDateValueChange(field, event) {
      const value = event.target.value;
      const operator = this.filters[field]?.operator || '$eq';
      this.updateFilter(field, operator, value);
    },

    // === Enum handlers ===
    getEnumSelectedCount(field) {
      const filter = this.filters[field];
      if (!filter || !Array.isArray(filter.value)) return 0;
      return filter.value.length;
    },

    isEnumOptionSelected(field, option) {
      const filter = this.filters[field];
      if (!filter || !Array.isArray(filter.value)) return false;
      return filter.value.includes(option);
    },

    toggleEnumOption(field, option) {
      const filter = this.filters[field];
      const current = Array.isArray(filter?.value) ? [...filter.value] : [];

      const index = current.indexOf(option);
      if (index >= 0) {
        current.splice(index, 1);
      } else {
        current.push(option);
      }

      this.updateFilter(field, '$in', current);
    },

    selectAllEnumOptions(field) {
      const options = this.getEnumOptions(field);
      this.updateFilter(field, '$in', [...options]);
    },

    clearEnumOptions(field) {
      this.updateFilter(field, '$in', []);
    },
  },

  template: `
    <!-- Action column: Clear All button -->
    <div :style="actionControlStyles" class="d-flex align-items-center">
      <button
        v-if="hasActiveFilters"
        @click="clearAll"
        class="btn btn-sm btn-outline-danger"
        title="Clear all filters"
      >
        <i class="bi bi-x"></i>
      </button>
    </div>

    <!-- Filter cell per field -->
    <div
      v-for="field in fields"
      :key="'filter-' + field"
      :style="fieldHeaderStyles"
      class="d-flex align-items-center"
    >
      <!-- String: simple regex input -->
      <template v-if="getFieldType(field) === 'string'">
        <input
          type="text"
          :value="filters[field]?.value || ''"
          @input="onStringInput(field, $event)"
          class="form-control form-control-sm"
          placeholder="Search..."
        />
      </template>

      <!-- Boolean: rotating button -->
      <template v-else-if="getFieldType(field) === 'boolean'">
        <button
          @click="rotateBooleanFilter(field)"
          class="btn btn-sm btn-outline-secondary"
          :title="getBooleanTitle(getBooleanState(field))"
        >
          <i :class="'bi ' + getBooleanIcon(getBooleanState(field))"></i>
        </button>
      </template>

      <!-- Number: input-group with operator selector -->
      <template v-else-if="getFieldType(field) === 'number'">
        <div class="input-group input-group-sm">
          <select
            :value="filters[field]?.operator || '$eq'"
            @change="onNumericOperatorChange(field, $event)"
            class="form-control flex-grow-0"
            style="flex-basis: min-content;"
          >
            <option v-for="op in comparisonOperators" :key="op.value" :value="op.value">
              {{ op.label }}
            </option>
          </select>
          <input
            type="number"
            :value="filters[field]?.value"
            @input="onNumericValueChange(field, $event)"
            class="form-control flex-grow-1"
            placeholder="..."
          />
        </div>
      </template>

      <!-- Date/DateTime: input-group with operator selector and date picker -->
      <template v-else-if="getFieldType(field) === 'date' || getFieldType(field) === 'datetime'">
        <div class="input-group input-group-sm">
          <select
            :value="filters[field]?.operator || '$eq'"
            @change="onDateOperatorChange(field, $event)"
            class="form-control flex-grow-0"
            style="flex-basis: min-content;"
          >
            <option v-for="op in comparisonOperators" :key="op.value" :value="op.value">
              {{ op.label }}
            </option>
          </select>
          <input
            :type="getDateInputType(getFieldType(field))"
            :value="filters[field]?.value || ''"
            @input="onDateValueChange(field, $event)"
            class="form-control flex-grow-1"
          />
        </div>
      </template>

      <!-- Enum: dropdown multi-select -->
      <template v-else-if="getFieldType(field) === 'enum'">
        <div class="dropdown">
          <button
            class="btn btn-sm btn-outline-secondary dropdown-toggle"
            type="button"
            data-bs-toggle="dropdown"
            data-bs-auto-close="outside"
            aria-expanded="false"
          >
            <template v-if="getEnumSelectedCount(field) === 0">
              All
            </template>
            <template v-else-if="getEnumSelectedCount(field) === getEnumOptions(field).length">
              All
            </template>
            <template v-else>
              {{ getEnumSelectedCount(field) }} selected
            </template>
          </button>
          <ul class="dropdown-menu" style="min-width: 150px;">
            <li class="px-2 py-1 d-flex gap-2">
              <button
                @click.stop="selectAllEnumOptions(field)"
                class="btn btn-sm btn-outline-secondary flex-fill"
              >
                All
              </button>
              <button
                @click.stop="clearEnumOptions(field)"
                class="btn btn-sm btn-outline-secondary flex-fill"
              >
                None
              </button>
            </li>
            <li><hr class="dropdown-divider"></li>
            <li v-for="option in getEnumOptions(field)" :key="option">
              <label class="dropdown-item d-flex align-items-center gap-2" style="cursor: pointer;">
                <input
                  type="checkbox"
                  class="form-check-input m-0"
                  :checked="isEnumOptionSelected(field, option)"
                  @change="toggleEnumOption(field, option)"
                />
                {{ option }}
              </label>
            </li>
          </ul>
        </div>
      </template>
    </div>
  `,
};
